<template lang="pug">
v-row.mb-6
  v-col(cols="12")
    TextBody(:text="infoText")
    GroupMemberSingleSelect#event-form__contact.organizer__select(
      :label="$t('components.event_form.contact_person_or_group')",
      isMandatory,
      :getOptions="getResults",
      :getOptionsParams="getResultParams",
      item-text="name",
      v-model="contact",
      @search="updateSearch",
      attach=".organizer__select",
      :hideNoData="true",
      required,
      :rules="contactRules",
      :specifySearch="specifyGroupSearch"
    )
</template>

<script lang="ts">
import { computed, defineComponent, inject, WritableComputedRef } from "vue"
import GroupMemberSingleSelect from "../GroupMemberSingleSelect.vue"
import TextBody from "../UI/TextBody.vue"
import { createNamespacedHelpers } from "vuex"
import { Group, GroupMember } from "suus-api"
import { TranslateResult } from "vue-i18n"
import EventFormService from "features/events/form/event-form.service"
import { EventForm } from "components/EventForm/EventForm.vue"
import { Rule } from "features/common/forms/form.service"
import { PanelIdentifier } from "features/events/form/event-form.types"
import useTranslate from "features/common/translate/concerns/useTranslate"
import MemberService from "features/member/member.service"
import { fetchGroupMembers, fetchGroups } from "features/group/group.functions"

export type GroupOrMember = GroupMember | Group | undefined
const { mapState: mapEventState, mapMutations: mapEventMutations } =
  createNamespacedHelpers("event")
const { mapGetters: mapMemberGetters } = createNamespacedHelpers("member")

type ResultParams = {
  group_id: number | undefined
  client_id: number | undefined
  level_id: number | undefined
}

const OrganizerFields = defineComponent({
  name: "OrganizerFields",
  components: { GroupMemberSingleSelect, TextBody },
  data() {
    return {
      groups: [] as Group[],
      members: [] as GroupMember[],
      searchParam: null as string | null,
    }
  },
  computed: {
    isEditForm(): boolean {
      return !!this.eventForm?.id
    },
    infoText(): TranslateResult {
      return this.$t("components.event_form.please_choose_contact")
    },
    getResultParams(): ResultParams {
      return {
        group_id: this.group?.id,
        client_id: this.group?.client_id,
        level_id: this.group?.level_id,
      }
    },
    contactRules(): any {
      return [
        (v) => !!v || this.$t("components.event_form.please_enter_contact"),
      ]
    },
    group(): Group {
      return this.eventForm?.group
    },
    specifyGroupSearch(): boolean {
      return this.groups?.length >= 100 || this.members?.length >= 20
    },
    ...mapEventState(["eventForm"]),
    ...mapMemberGetters(["memberLevelIds", "currentClient"]),
  },
  methods: {
    async getResults(params: ResultParams) {
      const { client_id, level_id } = params
      const groups = await fetchGroups(client_id, level_id)
      const groupMembers = await fetchGroupMembers(this.searchParam, level_id)
      return [...groupMembers, ...groups]
    },
    updateSearch(val) {
      this.searchParam = val
    },
    ...mapEventMutations(["updateEventForm"]),
  },
  setup() {
    const { t$ } = useTranslate("components.event_form")

    const eventFormService =
      inject<EventFormService<EventForm>>("EventFormService")

    const memberService: MemberService = inject("MemberService")

    const contactRule: Rule<EventForm, "contact"> = {
      field: "contact",
      message: t$(".validate_contact").toString(),
      selector: "event-form__contact",
      panelIdentifier: PanelIdentifier.GENERAL,
      action: (v) => !!v && ("name" in v ? !!v.name.length : true),
    }

    eventFormService.registerRule(contactRule as Rule<EventForm>)
    eventFormService.registerProperty({
      name: "contact",
    })

    eventFormService.registerProperty({
      name: "contact_type",
    })

    const setContactType = (contact: GroupOrMember) => {
      if (!contact) {
        eventFormService.setFormValue("contact_type", null)
      } else {
        // Only groups have a "type" attribute indicating the specific type of the Group
        const contact_type =
          !!contact && "type" in contact ? "Group" : "GroupMember"
        eventFormService.setFormValue("contact_type", contact_type)
      }
    }

    const contact: WritableComputedRef<GroupOrMember> = computed({
      get(): GroupOrMember {
        return eventFormService.getFormValue("contact").value
      },
      set(value: GroupOrMember) {
        eventFormService.setFormValue("contact", value)
        setContactType(value)
        eventFormService.validate()
      },
    })

    memberService.getCurrentGroupMember().then((member) => {
      if (member && contact.value == undefined) {
        contact.value = member
      }
    })

    return {
      contact,
    }
  },
})

export default OrganizerFields
</script>
